import { useState, useEffect } from 'react'
import { Box, Button, Typography, CircularProgress, Alert } from '@mui/material'
import Add from '@mui/icons-material/Add'
import PlayArrow from '@mui/icons-material/PlayArrow'
import Edit from '@mui/icons-material/Edit'
import Delete from '@mui/icons-material/Delete'
import { CSmartTable } from '@coreui/react-pro'
import { getCSmartTableColumnData } from 'src/consumer/helpers/utilFunctions'
import { searchPlayableExternalContent } from 'src/supportPortal/services/docSideService'
import { CONTENT_TYPES } from 'src/supportPortal/constants/docSideConstants'
import ContentDialog from './ContentDialog'

export default function ContentManagement({ context, product, role }) {
  const [loading, setLoading] = useState(false)
  const [content, setContent] = useState([])
  const [error, setError] = useState(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedContent, setSelectedContent] = useState(null)
  const [dialogMode, setDialogMode] = useState('add') // 'add', 'edit', 'view'

  // Load content on component mount or when product/role changes
  useEffect(() => {
    loadContent()
  }, [product.packageId, role.id])

  const loadContent = async () => {
    setLoading(true)
    setError(null)
    try {
      const searchCriteria = {
        roles: role.id,
        contextIds: context.id,
        productIds: product.id,
      }
      const response = await searchPlayableExternalContent(searchCriteria)
      if (response?.status === 200) {
        setContent(response.data || [])
      } else {
        setError('Failed to load content')
      }
    } catch (err) {
      setError('Failed to load content: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleAddNew = () => {
    setSelectedContent(null)
    setDialogMode('add')
    setDialogOpen(true)
  }

  const handleRowClick = (item) => {
    setSelectedContent(item)
    setDialogMode('view')
    setDialogOpen(true)
  }

  const handleEdit = (item, event) => {
    event.stopPropagation()
    setSelectedContent(item)
    setDialogMode('edit')
    setDialogOpen(true)
  }

  const handleDialogClose = () => {
    setDialogOpen(false)
    setSelectedContent(null)
  }

  const handleContentSaved = () => {
    handleDialogClose()
    loadContent() // Reload the table
  }

  // Table columns configuration
  const columns = [
    getCSmartTableColumnData({
      key: 'name',
      label: 'Name',
      _style: { width: '25%' },
    }),
    getCSmartTableColumnData({
      key: 'type',
      label: 'Type',
      _style: { width: '15%' },
    }),
    getCSmartTableColumnData({
      key: 'contentId',
      label: 'Content ID',
      _style: { width: '20%' },
    }),
    getCSmartTableColumnData({
      key: 'publishedTime',
      label: 'Published',
      _style: { width: '15%' },
    }),
    getCSmartTableColumnData({
      key: 'actions',
      label: 'Actions',
      _style: { width: '15%' },
      sorter: false,
      filter: false,
    }),
  ]

  // Scoped columns for custom rendering
  const scopedColumns = {
    type: (item) => (
      <td>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PlayArrow sx={{ fontSize: 16, color: 'primary.main' }} />
          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
            {item.type === CONTENT_TYPES.PLAYLIST ? 'Playlist' : 'Single Video'}
          </Typography>
        </Box>
      </td>
    ),
    contentId: (item) => (
      <td>
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            color: 'text.secondary',
            fontSize: '0.875rem',
          }}
        >
          {item.contentId || 'N/A'}
        </Typography>
      </td>
    ),
    publishedTime: (item) => (
      <td>
        <Typography variant="body2">
          {item.publishedTime
            ? (() => {
                const d = new Date(item.publishedTime)
                return Number.isNaN(d.getTime()) ? 'Not set' : d.toLocaleDateString()
              })()
            : 'Not set'}
        </Typography>
      </td>
    ),
    actions: (item) => (
      <td>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Edit />}
            onClick={(e) => handleEdit(item, e)}
          >
            Edit
          </Button>
        </Box>
      </td>
    ),
  }

  return (
    <Box>
      {/* Header with Add Button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          mb: 4,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Box>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontWeight: 700,
              letterSpacing: '-0.02em',
              color: 'text.primary',
              mb: 0.5,
            }}
          >
            Content Library
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            Manage playable external content for <strong>{role.title || role.name || role.id}</strong> in{' '}
            <strong>{product.title || product.packageId}</strong>
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddNew}
          disabled={loading}
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            px: 3,
            py: 1.5,
            fontWeight: 600,
            boxShadow: '0 4px 12px rgba(67, 56, 202, 0.3)',
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 1) 0%, rgba(67, 56, 202, 1) 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, rgba(79, 70, 229, 1) 0%, rgba(55, 48, 163, 1) 100%)',
              boxShadow: '0 6px 16px rgba(67, 56, 202, 0.4)',
            },
          }}
        >
          Add Content
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          {error}
        </Alert>
      )}

      {/* Content Table */}
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            mb: 3,
            borderRadius: 3,
            overflow: 'hidden',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
          }}
        >
          <CSmartTable
            items={content?.map((item) => ({
              ...item,
              name: item.name || '--',
            }))}
            columns={columns}
            scopedColumns={scopedColumns}
            loading={loading}
            tableProps={{
              hover: true,
              striped: true,
              responsive: true,
              style: { cursor: 'pointer' },
            }}
            itemsPerPage={50}
            pagination={content.length > 50}
            onRowClick={(item) => handleRowClick(item)}
          />
        </Box>
      )}

      {/* Empty State */}
      {!loading && content.length === 0 && !error && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 3,
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
          }}
        >
          <PlayArrow sx={{ fontSize: 64, color: 'text.disabled', mb: 2, opacity: 0.5 }} />
          <Typography
            variant="h6"
            color="text.secondary"
            gutterBottom
            sx={{ fontWeight: 600, mb: 1 }}
          >
            No content found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
            Get started by adding your first piece of content for this role.
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddNew}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(67, 56, 202, 0.3)',
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 1) 0%, rgba(67, 56, 202, 1) 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, rgba(79, 70, 229, 1) 0%, rgba(55, 48, 163, 1) 100%)',
                boxShadow: '0 6px 16px rgba(67, 56, 202, 0.4)',
              },
            }}
          >
            Add First Content
          </Button>
        </Box>
      )}

      {/* Content Dialog */}
      {dialogOpen && (
        <ContentDialog
          open={dialogOpen}
          mode={dialogMode}
          content={selectedContent}
          product={product}
          context={context}
          role={role}
          onClose={handleDialogClose}
          onSave={handleContentSaved}
        />
      )}
    </Box>
  )
}
